import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LayoutDashboard, Users, Inbox, FolderOpen, FileText, Receipt, Wallet, MessageSquare, Settings as SettingsIcon, LogOut, Calendar, Image, TrendingUp, Package, Send, ClipboardList, Trash2 } from 'lucide-react';
import api from './lib/api';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import Enquiries from './pages/Enquiries';
import Availability from './pages/Availability';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import QuotationBuilder from './pages/QuotationBuilder';
import Quotations from './pages/Quotations';
import QuotationView from './pages/QuotationView';
import Invoices from './pages/Invoices';
import InvoiceView from './pages/InvoiceView';
import InvoiceEdit from './pages/InvoiceEdit';
import Payouts from './pages/Payouts';
import Templates from './pages/Templates';
import SettingsPage from './pages/Settings';
import PublicInvoice from './pages/PublicInvoice';
import PublicQuotation from './pages/PublicQuotation';
import PublicConsultation from './pages/PublicConsultation';
import PublicConsent from './pages/PublicConsent';
import PublicPortfolioSet from './pages/PublicPortfolioSet';
import Trash from './pages/Trash';
import Portfolio from './pages/Portfolio';
import Expenses from './pages/Expenses';
import KitInventory from './pages/KitInventory';
import Reports from './pages/Reports';
import ContentCalendar from './pages/ContentCalendar';
import Outreach from './pages/Outreach';
import Consultations from './pages/Consultations';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = localStorage.getItem('rs_token');
    if (!t) { setLoading(false); return; }
    api.get('/auth/me').then(r => setUser(r.data)).catch(() => localStorage.removeItem('rs_token')).finally(() => setLoading(false));
  }, []);
  const login = async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    localStorage.setItem('rs_token', r.data.token);
    const me = await api.get('/auth/me');
    setUser(me.data);
  };
  const logout = () => { localStorage.removeItem('rs_token'); setUser(null); };
  return <AuthCtx.Provider value={{ user, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-[var(--muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const NAV = [
  { to: '/', label: 'Home', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { to: '/enquiries', label: 'Enquiries', icon: Inbox, testId: 'nav-enquiries' },
  { to: '/quotations', label: 'Quotes', icon: FileText, testId: 'nav-quotations' },
  { to: '/invoices', label: 'Invoices', icon: Receipt, testId: 'nav-invoices' },
  { to: '/projects', label: 'Projects', icon: FolderOpen, testId: 'nav-projects' },
];

const MORE_NAV = [
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/availability', label: 'Availability', icon: Calendar },
  { to: '/payouts', label: 'Artist Payouts', icon: Wallet },
  { to: '/portfolio', label: 'Portfolio', icon: Image },
  { to: '/consultations', label: 'Consultations', icon: ClipboardList },
  { to: '/expenses', label: 'Expenses', icon: TrendingUp },
  { to: '/kit', label: 'Kit', icon: Package },
  { to: '/content', label: 'Content', icon: MessageSquare },
  { to: '/outreach', label: 'Outreach', icon: Send },
  { to: '/reports', label: 'Reports', icon: TrendingUp },
  { to: '/templates', label: 'Templates', icon: MessageSquare },
  { to: '/trash', label: 'Trash', icon: Trash2 },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

function Shell({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout, user } = useAuth();
  return (
    <div className="app-shell">
      <aside className="side-nav overflow-y-auto">
        <div className="mb-6 px-2">
          <div className="serif text-[22px] leading-tight" style={{color:'var(--charcoal)'}}>Rishika</div>
          <div className="serif italic text-[13px]" style={{color:'var(--gold)'}}>Elegance Defined.</div>
          <div className="serif italic text-[13px]" style={{color:'var(--gold)'}}>Beauty Refined.</div>
        </div>
        {[...NAV, ...MORE_NAV].map(n => {
          const Icon = n.icon;
          const active = loc.pathname === n.to || (n.to !== '/' && loc.pathname.startsWith(n.to));
          return (
            <Link key={n.to} to={n.to} className={active ? 'active' : ''} data-testid={n.testId || `side-${n.to.slice(1)}`}>
              <Icon size={16} /> {n.label}
            </Link>
          );
        })}
        <button onClick={() => { logout(); nav('/login'); }} className="mt-4 flex items-center gap-2 px-3 py-2 text-sm text-[var(--muted)] hover:text-[var(--overdue)]" data-testid="logout-btn">
          <LogOut size={14} /> Sign out
        </button>
      </aside>

      <div className="main-with-side">
        <header className="top-nav sticky top-0 z-30 px-5 py-3 lg:hidden flex items-center justify-between">
          <div>
            <div className="serif text-[18px] leading-tight" style={{color:'var(--charcoal)'}}>Rishika</div>
            <div className="serif italic text-[11px]" style={{color:'var(--gold)'}}>Elegance Defined. Beauty Refined.</div>
          </div>
          <Link to="/settings" className="text-[var(--muted)]" data-testid="mobile-settings-link"><SettingsIcon size={18} /></Link>
        </header>
        <main className="pad-body">{children}</main>
      </div>

      <nav className="bottom-nav lg:hidden">
        {NAV.map(n => {
          const Icon = n.icon;
          const active = loc.pathname === n.to || (n.to !== '/' && loc.pathname.startsWith(n.to));
          return (
            <Link key={n.to} to={n.to} className={active ? 'active' : ''} data-testid={`bottom-${n.testId}`}>
              <Icon /> <span>{n.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/public/invoice/:id" element={<PublicInvoice />} />
          <Route path="/public/quotation/:id" element={<PublicQuotation />} />
          <Route path="/public/consultation/:project_id" element={<PublicConsultation />} />
          <Route path="/public/consent/:project_id" element={<PublicConsent />} />
          <Route path="/public/portfolio-set/:id" element={<PublicPortfolioSet />} />
          <Route path="/*" element={
            <Protected>
              <Shell>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/enquiries" element={<Enquiries />} />
                  <Route path="/availability" element={<Availability />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                  <Route path="/quotations" element={<Quotations />} />
                  <Route path="/quotations/new" element={<QuotationBuilder />} />
                  <Route path="/quotations/:id" element={<QuotationView />} />
                  <Route path="/quotations/:id/edit" element={<QuotationBuilder />} />
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/invoices/new" element={<InvoiceEdit />} />
                  <Route path="/invoices/:id" element={<InvoiceView />} />
                  <Route path="/invoices/:id/edit" element={<InvoiceEdit />} />
                  <Route path="/payouts" element={<Payouts />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/consultations" element={<Consultations />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/kit" element={<KitInventory />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/content" element={<ContentCalendar />} />
                  <Route path="/outreach" element={<Outreach />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/trash" element={<Trash />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Routes>
              </Shell>
            </Protected>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
